import axios from 'axios';
import { BASE_URL } from '../../api/helpers';
import { TrainingDataset } from '../../api/loraRuntime';
import { MediaLibraryItem } from '../../api/media';
import {
  approveReviewImageToProfile,
  mediaItemDisplayUrl,
} from '../../api/datasetOrchestration';

/**
 * Dataset-curation edit API, co-located with the curation UI. These hit the Go
 * backend dataset endpoints (PATCH /datasets/:id, PATCH /datasets/:id/items/:itemId)
 * which proxy the modal bridge, plus the bulk "add from media library" path that
 * reuses the proven per-image from-media append endpoint.
 */

/** Body for editing a single dataset item's training caption, tags, and/or its
 * `excluded` (disabled-for-training) flag. */
export interface UpdateDatasetItemBody {
  caption?: string;
  tags?: string[];
  excluded?: boolean;
}

/** Character context the vision model needs to write an on-brand LoRA caption. */
export interface CaptionContext {
  subjectToken: string;
  characterName?: string;
  /** Persona / style description of the influencer (the character style_prompt). */
  persona?: string;
}

/**
 * Vision-capable models a user may pick for auto-captioning (Settings →
 * Auto-captioning model). This MUST stay in sync with the backend allowlist in
 * captionDatasetImage.go (captionModelAllowlist): the backend rejects anything not
 * on its list, and the credit-guard fails closed (402) for any model the credits
 * registry does not price. Keep this to commonly-priced vision models.
 */
export interface CaptionModelOption {
  /** OpenRouter model id sent to the backend `model` field. */
  id: string;
  /** Human label shown in the settings dropdown. */
  label: string;
}

export const CAPTION_MODEL_OPTIONS: CaptionModelOption[] = [
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (default, fast & cheap)' },
  { id: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (higher quality)' },
  { id: 'openai/gpt-4o', label: 'GPT-4o' },
  { id: 'openai/gpt-4o-mini', label: 'GPT-4o mini' },
  { id: 'anthropic/claude-sonnet-4.5', label: 'Claude Sonnet 4.5' },
];

/** Default auto-captioning model — matches the backend CaptionModel default. */
export const DEFAULT_CAPTION_MODEL = 'google/gemini-2.5-flash';

/** localStorage key the Settings panel persists the chosen caption model under. */
export const CAPTION_MODEL_STORAGE_KEY = 'datasetCaptionModel';

/**
 * Reads the persisted auto-captioning model, falling back to the default when
 * unset, unparseable, or no longer on the allowlist. Read at caption time (not
 * cached in React state) so the freshest setting is always used — the Settings
 * modal and the curation view live in separate component trees.
 */
export const getStoredCaptionModel = (): string => {
  try {
    const raw = localStorage.getItem(CAPTION_MODEL_STORAGE_KEY);
    if (!raw) return DEFAULT_CAPTION_MODEL;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string' && CAPTION_MODEL_OPTIONS.some((o) => o.id === parsed)) {
      return parsed;
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT_CAPTION_MODEL;
};

/**
 * Auto-captions ONE dataset image with a vision model (OpenRouter gemini-flash,
 * server-side). Pass the image's bridge `ref` (a /lora/files path — what dataset
 * items carry) and the character context; the backend loads the image bytes and
 * returns a single LoRA-appropriate training caption that leads with the trigger
 * token and describes what VARIES (pose, outfit, setting, lighting, framing).
 *
 * This is an external AI call, so it is only invoked from the explicit
 * "Auto-caption" / "Regenerate" actions and the fill-placeholders-on-add path —
 * never on every render. One call captions one image so a single failure never
 * aborts a batch.
 *
 * `model` is the OPTIONAL user-selected vision model (Settings → Auto-captioning
 * model). When omitted the backend uses its default (gemini-2.5-flash). A model
 * the backend doesn't allowlist — or one the credits registry can't price —
 * surfaces as a per-image failure the bulk loop tolerates.
 */
export const captionDatasetImage = async (
  ref: string,
  context: CaptionContext,
  model?: string
): Promise<string> => {
  const body: Record<string, string> = {
    ref,
    subject_token: context.subjectToken,
    character_name: context.characterName ?? '',
    persona: context.persona ?? '',
  };
  if (model && model.trim()) {
    body.model = model.trim();
  }
  const res = await axios.post<{ caption: string }>(`${BASE_URL}/datasets/caption`, body);
  return res.data.caption;
};

/**
 * The bridge's append-time placeholder caption is `"<token> curated dataset
 * image"` (lora_runtime.py) — and an empty caption is also a placeholder. These
 * are the ONLY captions the auto-on-add / "auto-caption all" flow may overwrite;
 * a real, user-edited caption must never be clobbered.
 */
export const isPlaceholderCaption = (caption: string, subjectToken: string): boolean => {
  const trimmed = (caption ?? '').trim();
  if (trimmed === '') return true;
  const token = (subjectToken ?? '').trim();
  if (token && trimmed === `${token} curated dataset image`) return true;
  // Defend against an older/tokenless placeholder too.
  if (trimmed === 'curated dataset image') return true;
  return false;
};

/**
 * Edits one dataset item's training caption/tags. The caption is the exact text
 * the GPU training loop encodes for that image (it is passed straight into
 * `encode_prompt`), so this is how a user curates what each image teaches the
 * LoRA before training. Returns the updated dataset (recomputed validation).
 */
export const updateDatasetItem = async (
  datasetId: string,
  itemId: string,
  body: UpdateDatasetItemBody
): Promise<TrainingDataset> => {
  const res = await axios.patch<TrainingDataset>(
    `${BASE_URL}/datasets/${datasetId}/items/${itemId}`,
    body
  );
  return res.data;
};

/**
 * Toggles one dataset item's persistent `excluded` (disabled-for-training) flag.
 * An excluded image is skipped by every future training run for this dataset and
 * rendered dimmed in the UI. Returns the updated dataset.
 */
export const setDatasetItemExcluded = async (
  datasetId: string,
  itemId: string,
  excluded: boolean
): Promise<TrainingDataset> => updateDatasetItem(datasetId, itemId, { excluded });

/**
 * Persists the dataset-level review gate. This is separate from per-image
 * inclusion: only an explicitly approved standalone dataset appears in Training.
 */
export const setDatasetApprovedForTraining = async (
  datasetId: string,
  approvedForTraining: boolean
): Promise<TrainingDataset> => {
  const res = await axios.patch<TrainingDataset>(`${BASE_URL}/datasets/${datasetId}`, {
    approved_for_training: approvedForTraining,
  });
  return res.data;
};

/**
 * Edits the dataset's trigger word (subject_token). Changing it re-runs
 * validation, so per-item "caption does not include the trigger word" warnings
 * recompute against the new token. Returns the updated dataset.
 */
export const updateDatasetTriggerWord = async (
  datasetId: string,
  subjectToken: string
): Promise<TrainingDataset> => {
  const res = await axios.patch<TrainingDataset>(`${BASE_URL}/datasets/${datasetId}`, {
    subject_token: subjectToken,
  });
  return res.data;
};

/**
 * Renames the dataset (its human label only — `name`). The trigger word and the
 * dataset's items/files are untouched. Returns the updated dataset.
 */
export const renameDataset = async (
  datasetId: string,
  name: string
): Promise<TrainingDataset> => {
  const res = await axios.patch<TrainingDataset>(`${BASE_URL}/datasets/${datasetId}`, {
    name,
  });
  return res.data;
};

/**
 * Appends a batch of media-library items (picked or freshly uploaded via the
 * AttachmentLibraryModal) to a character's persistent TrainingDataset. Reuses the
 * per-image from-media append endpoint (which resolves + downloads bytes
 * server-side, avoiding browser CORS on signed urls) once per item, sequentially
 * so the dataset's item list and counts stay consistent. The bridge returns the
 * updated dataset after each append, so the LAST successful response is the
 * authoritative final state.
 *
 * Items that cannot be resolved (no media id and no url) are skipped and reported
 * in `skipped` so the UI can surface a partial-success message.
 */
export interface AddImagesResult {
  dataset: TrainingDataset | null;
  added: number;
  skipped: number;
}

export const addImagesFromMediaItems = async (
  characterId: string,
  items: MediaLibraryItem[]
): Promise<AddImagesResult> => {
  let dataset: TrainingDataset | null = null;
  let added = 0;
  let skipped = 0;
  for (const item of items) {
    const mediaId = item.media_id || item.id;
    const url = mediaItemDisplayUrl(item);
    if (!mediaId && !url) {
      skipped += 1;
      continue;
    }
    // approveReviewImageToProfile takes { media_id?, url? } and appends ONE image
    // to the bound dataset, returning the updated dataset.
    dataset = await approveReviewImageToProfile(characterId, { media_id: mediaId, url });
    added += 1;
  }
  return { dataset, added, skipped };
};
